import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Truck, DollarSign, Activity,
  RefreshCw, Trash2, Search, X, AlertCircle,
  Wifi, Radio, WifiOff,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");


const VEHICLE_ICONS  = { moto: "🏍️", bicicleta: "🚲", carro: "🚗", camion: "🚛", otro: "🚐" };

const STATUS_META = {
  disponible: { label: "Disponible", dot: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700" },
  ocupado:    { label: "En servicio",  dot: "bg-amber-400",   badge: "bg-amber-100 text-amber-700"   },
  offline:    { label: "Offline",     dot: "bg-gray-400",    badge: "bg-gray-100 text-gray-500"    },
};

function RyderCard({ ryder, onRemove, removing }) {
  const [confirm, setConfirm] = useState(false);
  const meta = STATUS_META[ryder.status] ?? STATUS_META.offline;
  const name = ryder.user?.name || "Sin nombre";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-3"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white font-bold text-sm">
            {name.slice(0, 2).toUpperCase()}
          </div>
          <span className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${meta.dot}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-900 truncate">{name}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${meta.badge}`}>{meta.label}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {VEHICLE_ICONS[ryder.vehicleType] ?? "🚐"} {ryder.vehicleType}
            {ryder.vehiclePlate ? ` · ${ryder.vehiclePlate}` : ""}
          </p>
        </div>
        <div className="shrink-0">
          {!confirm ? (
            <button
              onClick={() => setConfirm(true)}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition"
              title="Remover de la empresa"
            >
              <Trash2 size={14} />
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-red-600 font-semibold">¿Remover?</span>
              <button
                onClick={() => { onRemove(ryder.id); setConfirm(false); }}
                disabled={removing === ryder.id}
                className="text-[10px] font-bold px-2 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >Sí</button>
              <button
                onClick={() => setConfirm(false)}
                className="text-[10px] font-bold px-2 py-1 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              >No</button>
            </div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-50">
        <div className="text-center">
          <p className="text-sm font-black text-gray-900">{ryder.deliveriesToday ?? 0}</p>
          <p className="text-[10px] text-gray-400">Hoy</p>
        </div>
        <div className="text-center">
          <p className="text-sm font-black text-gray-900">{ryder.totalDeliveries ?? 0}</p>
          <p className="text-[10px] text-gray-400">Total</p>
        </div>
        <div className="text-center">
          <p className="text-sm font-black text-gray-900">{Number(ryder.rating ?? 0).toFixed(1)}★</p>
          <p className="text-[10px] text-gray-400">Rating</p>
        </div>
      </div>

      {/* Earnings today */}
      {(ryder.earningsToday ?? 0) > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5">
          <DollarSign size={11} />
          <span className="font-semibold">${(ryder.earningsToday ?? 0).toLocaleString()} ganancias hoy</span>
        </div>
      )}
    </motion.div>
  );
}

export default function DeliveryRyders() {
  const [company,    setCompany]    = useState(null);
  const [ryders,     setRyders]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [removing,   setRemoving]   = useState(null);
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("all"); // all | disponible | ocupado | offline

  const fetchCompany = useCallback(async () => {
    try {
      const r = await API.get("/delivery-companies/me");
      return r.data?.data?.company ?? r.data?.company;
    } catch { return null; }
  }, []);

  const fetchRyders = useCallback(async (companyId) => {
    try {
      const r = await API.get(`/delivery-companies/${companyId}/ryders`);
      setRyders(r.data?.data?.ryders ?? []);
    } catch { toast.error("Error al cargar ryders"); }
  }, []);

  useEffect(() => {
    fetchCompany().then(comp => {
      setCompany(comp);
      if (comp?.id) fetchRyders(comp.id).finally(() => setLoading(false));
      else setLoading(false);
    });
  }, [fetchCompany, fetchRyders]);

  const refresh = async () => {
    if (!company?.id) return;
    setRefreshing(true);
    await fetchRyders(company.id);
    setRefreshing(false);
  };

  const handleRemove = async (ryderId) => {
    setRemoving(ryderId);
    try {
      await API.delete(`/delivery-companies/${company.id}/ryders/${ryderId}`);
      toast.success("Ryder removido de la empresa");
      setRyders(prev => prev.filter(r => r.id !== ryderId));
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al remover ryder");
    } finally { setRemoving(null); }
  };

  const searchLower = search.toLowerCase();
  const filtered = ryders
    .filter(r => filter === "all" || r.status === filter)
    .filter(r =>
      !search || (r.name || r.user?.name || "").toLowerCase().includes(searchLower)
    );

  const disponibles = ryders.filter(r => r.status === "disponible").length;
  const ocupados    = ryders.filter(r => r.status === "ocupado").length;
  const offline     = ryders.filter(r => r.status === "offline").length;

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <Truck size={20} className="text-indigo-600" />
            Mis Ryders
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">{ryders.length} ryders en tu empresa</p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition disabled:opacity-50 shadow-sm"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {/* Status summary pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("all")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === "all" ? "bg-indigo-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <Activity size={11} />Todos ({ryders.length})
        </button>
        <button onClick={() => setFilter("disponible")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === "disponible" ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <Wifi size={11} />Disponibles ({disponibles})
        </button>
        <button onClick={() => setFilter("ocupado")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === "ocupado" ? "bg-amber-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <Radio size={11} />En servicio ({ocupados})
        </button>
        <button onClick={() => setFilter("offline")} className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition ${filter === "offline" ? "bg-gray-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
          <WifiOff size={11} />Offline ({offline})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="h-40 bg-white rounded-2xl animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 bg-white rounded-2xl border border-gray-100">
          <AlertCircle size={36} className="mb-3 text-gray-200" />
          <p className="font-semibold text-gray-500">
            {ryders.length === 0 ? "Aún no tienes ryders en tu empresa" : "Sin resultados"}
          </p>
          <p className="text-xs mt-1">
            {ryders.length === 0
              ? "Invita ryders desde el panel de Solicitudes"
              : "Prueba con otro filtro o término de búsqueda"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {filtered.map(r => (
              <RyderCard key={r.id} ryder={r} onRemove={handleRemove} removing={removing} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
